import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './Card';
import { useRecentCalls } from '../hooks/useRecentCalls';

function formatDuration(seconds: number, t: (key: string) => string): string {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs} ${t('recent_calls.seconds')}`;
  return `${mins} ${t('recent_calls.minutes')} ${secs} ${t('recent_calls.seconds')}`;
}

function formatRelativeTime(isoString: string | null, t: (key: string) => string): string {
  if (!isoString) return '—';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('recent_calls.just_now');
  if (mins < 60) return t('recent_calls.minutes_ago', { minutes: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('recent_calls.hours_ago', { hours });
  return new Date(isoString).toLocaleDateString();
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'completed' || status === 'ended'
      ? 'success'
      : status === 'failed' || status === 'error'
      ? 'danger'
      : 'warning';
  return <span className={`badge bg-${cls}`}>{status}</span>;
}

function ChannelBadge({ type }: { type: string }) {
  const icon =
    type === 'telegram'
      ? 'bi-telegram'
      : type === 'whatsapp'
      ? 'bi-whatsapp'
      : 'bi-telephone';
  return (
    <span>
      <i className={`bi ${icon} me-1`} />
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[1, 2, 3, 4, 5].map((i) => (
        <td key={i}>
          <div
            style={{
              height: '14px',
              background: '#e9ecef',
              borderRadius: '4px',
              width: `${60 + (i * 10) % 30}%`,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

export function RecentCallsTable() {
  const { t } = useTranslation();
  const { calls, isLoading, error } = useRecentCalls(10);

  return (
    <Card title={t('recent_calls.title')} collapsible className="h-100">
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>{t('recent_calls.time')}</th>
              <th>{t('recent_calls.type')}</th>
              <th>{t('recent_calls.duration')}</th>
              <th>{t('recent_calls.agent')}</th>
              <th>{t('recent_calls.status')}</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={5} className="text-danger">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {error}
                </td>
              </tr>
            ) : isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : calls.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted text-center">
                  {t('recent_calls.no_recent_calls')}
                </td>
              </tr>
            ) : (
              calls.map((call) => (
                <tr key={call.callId}>
                  <td>{formatRelativeTime(call.startedAt, t)}</td>
                  <td>
                    <ChannelBadge type={call.channelType} />
                  </td>
                  <td>{formatDuration(call.duration, t)}</td>
                  <td>{call.agentName}</td>
                  <td>
                    <StatusBadge status={call.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2">
        <Link to="/admin/call-logs" className="text-sm">
          {t('recent_calls.view_all')} → <i className="bi bi-arrow-right" />
        </Link>
      </div>
    </Card>
  );
}
